/**
 * Demo auth client.
 *
 * Replaces the build-time baked-in bearer token. The flow:
 *   1. User submits the password gate → `login(password)` POSTs it to the
 *      login Lambda, which returns a short-lived Cognito access token.
 *   2. Token + expiry are stored in sessionStorage so a page reload inside
 *      the same session stays authenticated until the token expires.
 *   3. Chat + voice clients read the token via `getStoredToken()`.
 *   4. On expiry (or a 401 from any API call) `notifyExpired()` clears the
 *      token and dispatches `gbn:auth-expired` so App can sign the user out.
 */

const TOKEN_KEY = 'gbn_token';
const EXP_KEY = 'gbn_token_exp'; // epoch ms
const EXPIRY_SKEW_MS = 30_000; // treat as expired 30s early to avoid races

export const AUTH_EXPIRED_EVENT = 'gbn:auth-expired';

function loginUrl(): string | undefined {
  return import.meta.env.VITE_AUTH_LOGIN_URL || undefined;
}

/** True when a real login endpoint is configured (deployed mode). */
export function isServerLoginConfigured(): boolean {
  return !!loginUrl();
}

interface LoginResponse {
  access_token: string;
  expires_in: number;
  expires_at?: number; // epoch seconds
}

/** Exchange the demo password for a short-lived token. Throws on failure. */
export async function login(password: string): Promise<void> {
  const url = loginUrl();
  if (!url) throw new Error('login endpoint not configured');

  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });

  if (resp.status === 401) throw new Error('Incorrect password. Please try again.');
  if (!resp.ok) throw new Error(`login failed (${resp.status})`);

  const data = (await resp.json()) as LoginResponse;
  if (!data.access_token) throw new Error('login response missing token');

  const expiresAtMs =
    (data.expires_at ? data.expires_at * 1000 : Date.now() + data.expires_in * 1000);
  sessionStorage.setItem(TOKEN_KEY, data.access_token);
  sessionStorage.setItem(EXP_KEY, String(expiresAtMs));
}

/** Epoch ms when the current token expires, or null if none. */
export function getTokenExpiry(): number | null {
  const raw = sessionStorage.getItem(EXP_KEY);
  if (!raw) return null;
  const ms = Number(raw);
  return Number.isFinite(ms) ? ms : null;
}

/** Return the token if present and not (nearly) expired, else null. */
export function getStoredToken(): string | null {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const exp = getTokenExpiry();
  if (!token || exp === null) return null;
  if (Date.now() >= exp - EXPIRY_SKEW_MS) {
    clearToken();
    return null;
  }
  return token;
}

export function isAuthenticated(): boolean {
  return getStoredToken() !== null;
}

function clearToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(EXP_KEY);
}

/** Clear auth state without notifying (explicit user sign-out). */
export function logout(): void {
  clearToken();
}

/**
 * Clear the token and broadcast expiry so the app can sign the user out.
 * Call this on token expiry or when an API call returns 401.
 */
export function notifyExpired(): void {
  clearToken();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
  }
}
